/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import { decorate, observable, action, computed, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Icon } from 'semantic-ui-react';

/**
 * A modal component for increasing the root EBS volume size of an EC2 workspace.
 * Works with SC stacks via function props for API calls, mirroring the structure
 * of ChangeInstanceTypeModal.
 *
 * Props:
 *  - open: boolean controlling modal visibility
 *  - onClose: callback to close the modal
 *  - environment: the environment object (must have .id)
 *  - onConfirm: callback(targetSizeGB) invoked on confirm, returns a Promise
 *  - getStorageInfo: async function(envId) that returns the storage info object
 */
class ModifyStorageSizeModal extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.loading = false;
      this.submitting = false;
      this.error = '';
      this.storageInfo = null;
      this.targetSizeGB = '';
    });
  }

  componentDidMount() {
    if (this.props.open) {
      this.fetchStorageInfo();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.fetchStorageInfo();
    }
  }

  fetchStorageInfo = async () => {
    const { environment, getStorageInfo } = this.props;
    runInAction(() => {
      this.loading = true;
      this.error = '';
      this.storageInfo = null;
      this.targetSizeGB = '';
    });

    try {
      const info = await getStorageInfo(environment.id);
      runInAction(() => {
        this.storageInfo = info || null;
        this.targetSizeGB = info ? info.currentSizeGB : '';
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || 'Failed to load storage information. Please try again.';
        this.loading = false;
      });
    }
  };

  handleSizeChange = (event) => {
    const parsed = parseInt(event.target.value, 10);
    runInAction(() => {
      this.targetSizeGB = Number.isNaN(parsed) ? '' : parsed;
    });
  };

  handlePreset = (delta) => {
    const { storageInfo, targetSizeGB } = this;
    if (!storageInfo) return;
    const { currentSizeGB, maxSizeGB } = storageInfo;
    const base = Number.isFinite(targetSizeGB) ? targetSizeGB : currentSizeGB;
    const next = Math.min(Math.max(base + delta, currentSizeGB), maxSizeGB);
    runInAction(() => {
      this.targetSizeGB = next;
    });
  };

  handleConfirm = async () => {
    runInAction(() => {
      this.submitting = true;
      this.error = '';
    });

    try {
      await this.props.onConfirm(this.targetSizeGB);
      runInAction(() => {
        this.submitting = false;
      });
      this.props.onClose();
    } catch (err) {
      runInAction(() => {
        this.error = err.message || 'Failed to modify storage. Please try again.';
        this.submitting = false;
      });
    }
  };

  handleClose = () => {
    if (!this.submitting) {
      this.props.onClose();
    }
  };

  get canSubmit() {
    const { storageInfo, targetSizeGB, submitting } = this;

    if (submitting) return false;
    if (!storageInfo) return false;

    const { modificationCooldown, currentSizeGB, maxSizeGB } = storageInfo;

    // Disable if a modification is currently in progress
    if (modificationCooldown && modificationCooldown.currentModificationInProgress) return false;

    // Disable if at cooldown limit
    if (modificationCooldown && modificationCooldown.remainingModifications === 0) return false;

    // Validate size
    if (targetSizeGB <= currentSizeGB) return false;
    if (targetSizeGB > maxSizeGB) return false;

    return true;
  }

  getValidationError() {
    const { storageInfo, targetSizeGB } = this;
    if (!storageInfo) return '';

    const { currentSizeGB, maxSizeGB } = storageInfo;

    if (targetSizeGB === '' || targetSizeGB === null || Number.isNaN(targetSizeGB)) {
      return 'Please enter a valid size.';
    }
    if (targetSizeGB <= currentSizeGB) {
      return `New size must be greater than current size (${currentSizeGB} GB).`;
    }
    if (targetSizeGB > maxSizeGB) {
      return `Size cannot exceed ${maxSizeGB} GB.`;
    }
    return '';
  }

  renderLoading() {
    return (
      <div className="mss-modal-loading">
        <Icon name="spinner" loading size="large" />
        <span>Loading storage information...</span>
      </div>
    );
  }

  renderError() {
    if (!this.error) return null;
    return (
      <div className="mss-modal-error" style={{ marginBottom: '16px' }}>
        <Icon name="exclamation triangle" />
        <div>
          <strong>Error:</strong> {this.error}
        </div>
      </div>
    );
  }

  renderUtilizationFill() {
    const { storageInfo } = this;
    const { diskUtilizationGB, currentSizeGB } = storageInfo;
    if (diskUtilizationGB == null || !currentSizeGB) return null;

    const ratio = diskUtilizationGB / currentSizeGB;
    const pct = Math.min(100, Math.round(ratio * 100));

    let level = 'low';
    if (ratio >= 0.8) {
      level = 'high';
    } else if (ratio >= 0.5) {
      level = 'medium';
    }

    return (
      <div className="mss-modal-utilization-bar">
        <div className={`mss-modal-utilization-fill ${level}`} style={{ width: `${pct}%` }} />
      </div>
    );
  }

  renderStorageInfo() {
    const { storageInfo } = this;
    const { currentSizeGB, diskUtilizationGB, instanceType, modificationCooldown } = storageInfo;

    const utilizationUnavailable = diskUtilizationGB == null;

    return (
      <div className="mss-modal-info-grid">
        <div className="mss-modal-info-item">
          <div className="mss-modal-info-label">Current Size</div>
          <div className="mss-modal-info-value">
            {currentSizeGB}
            <span className="mss-modal-info-unit">GB</span>
          </div>
        </div>
        <div className="mss-modal-info-item">
          <div className="mss-modal-info-label">Disk Utilisation</div>
          <div className="mss-modal-info-value">
            {utilizationUnavailable ? (
              'Utilisation unavailable'
            ) : (
              <>
                {diskUtilizationGB.toFixed(1)}
                <span className="mss-modal-info-unit">GB</span>
              </>
            )}
          </div>
          {!utilizationUnavailable && this.renderUtilizationFill()}
        </div>
        <div className="mss-modal-info-item">
          <div className="mss-modal-info-label">Instance Type</div>
          <div className="mss-modal-info-value">
            <code className="mss-modal-instance-code">{instanceType ? instanceType.split('/')[0] : 'N/A'}</code>
          </div>
        </div>
        <div className="mss-modal-info-item">
          <div className="mss-modal-info-label">Modifications Left</div>
          <div
            className={`mss-modal-info-value${
              modificationCooldown && modificationCooldown.remainingModifications === 0
                ? ' critical'
                : modificationCooldown && modificationCooldown.remainingModifications <= 1
                ? ' warning'
                : ''
            }`}
          >
            {modificationCooldown ? modificationCooldown.remainingModifications : 'N/A'}
            {modificationCooldown && <span className="mss-modal-info-unit">of 4</span>}
          </div>
        </div>
      </div>
    );
  }

  renderCooldownInfo() {
    const { storageInfo } = this;
    if (!storageInfo || !storageInfo.modificationCooldown) return null;

    const {
      remainingModifications,
      nextModificationAvailableAt,
      currentModificationInProgress,
    } = storageInfo.modificationCooldown;

    // Show in-progress banner if modification is currently running
    if (currentModificationInProgress) {
      return (
        <div className="mss-modal-in-progress">
          <Icon name="sync" className="mss-modal-in-progress-icon" />
          <span className="mss-modal-in-progress-text">
            A storage modification is currently in progress. Please wait for it to complete.
          </span>
        </div>
      );
    }

    // Show warning if at limit
    if (remainingModifications === 0 && nextModificationAvailableAt) {
      const availableTime = new Date(nextModificationAvailableAt).toLocaleString();
      return (
        <div className="mss-modal-cooldown-warning">
          <Icon name="warning sign" className="mss-modal-cooldown-warning-icon" />
          <span className="mss-modal-cooldown-warning-text">
            <strong>Modification limit reached.</strong> You have used all 4 modifications in the last 24 hours. Next
            modification available at {availableTime}.
          </span>
        </div>
      );
    }

    // Show warning if only 1 remaining
    if (remainingModifications === 1) {
      return (
        <div className="mss-modal-cooldown-warning">
          <Icon name="warning sign" className="mss-modal-cooldown-warning-icon" />
          <span className="mss-modal-cooldown-warning-text">
            <strong>Warning:</strong> Only 1 modification remaining in the next 24 hours. Choose your target size
            carefully.
          </span>
        </div>
      );
    }

    // The informational remaining count is now shown as an info box in the grid;
    // only alert-level banners (in-progress, limit reached, 1 remaining) are shown here.
    return null;
  }

  renderSizeSection() {
    const { storageInfo, targetSizeGB } = this;
    const { currentSizeGB, maxSizeGB } = storageInfo;

    // Storage already at (or above) the maximum allowed size
    if (currentSizeGB >= maxSizeGB) {
      return (
        <div className="mss-modal-cooldown-warning">
          <Icon name="info circle" className="mss-modal-cooldown-warning-icon" />
          <span className="mss-modal-cooldown-warning-text">
            Storage is already at the maximum allowed size ({maxSizeGB} GB) and cannot be increased further.
          </span>
        </div>
      );
    }

    const validationError = this.getValidationError();

    return (
      <div className="mss-modal-size-input-wrapper">
        <label className="mss-modal-size-label" htmlFor="mss-modal-size-input">
          New Storage Size (GB)
        </label>
        <div className="mss-modal-size-input-row">
          <input
            id="mss-modal-size-input"
            type="number"
            className={`mss-modal-size-input${validationError ? ' error' : ''}`}
            value={targetSizeGB}
            min={currentSizeGB + 1}
            max={maxSizeGB}
            onChange={this.handleSizeChange}
          />
        </div>
        <div className={`mss-modal-size-hint${validationError ? ' error' : ''}`}>
          {validationError || `Valid range: ${currentSizeGB + 1} GB - ${maxSizeGB} GB`}
        </div>
        <div className="mss-modal-presets">
          {[-100, -50, 50, 100, 200].map((delta) => {
            const disabled = delta < 0 ? targetSizeGB <= currentSizeGB : targetSizeGB >= maxSizeGB;
            return (
              <button
                key={delta}
                type="button"
                className="mss-modal-preset-btn"
                onClick={() => this.handlePreset(delta)}
                disabled={disabled}
              >
                {delta > 0 ? `+${delta}` : delta} GB
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  renderContent() {
    if (this.loading) return this.renderLoading();

    return (
      <>
        {this.renderError()}
        {this.storageInfo && (
          <>
            {this.renderCooldownInfo()}
            {this.renderStorageInfo()}
            {this.renderSizeSection()}
          </>
        )}
      </>
    );
  }

  render() {
    const { open } = this.props;
    if (!open) return null;

    const confirmDisabled = !this.canSubmit || this.loading;

    return ReactDOM.createPortal(
      <>
        <div className="mss-modal-backdrop" onClick={this.submitting ? undefined : this.handleClose} />
        <div className="mss-modal">
          <div className="mss-modal-header">
            <div className="mss-modal-header-title">
              <Icon name="hdd" style={{ color: '#2563eb', marginRight: '8px' }} />
              Modify Storage Size
            </div>
            {!this.submitting && (
              <button className="mss-modal-close" onClick={this.handleClose} aria-label="Close">
                <Icon name="close" style={{ margin: 0 }} />
              </button>
            )}
          </div>
          <div className="mss-modal-body">{this.renderContent()}</div>
          <div className="mss-modal-footer">
            <button
              className="workspace-action-btn workspace-action-btn-secondary"
              onClick={this.handleClose}
              disabled={this.submitting}
            >
              Cancel
            </button>
            <button
              className="workspace-action-btn workspace-action-btn-restart"
              onClick={this.handleConfirm}
              disabled={confirmDisabled}
            >
              {this.submitting && <Icon name="spinner" loading style={{ lineHeight: 1, height: '1em', margin: 0 }} />}
              {!this.submitting && <Icon name="check" style={{ lineHeight: 1, height: '1em', margin: 0 }} />}
              Confirm
            </button>
          </div>
        </div>
      </>,
      document.body,
    );
  }
}

decorate(ModifyStorageSizeModal, {
  loading: observable,
  submitting: observable,
  error: observable,
  storageInfo: observable,
  targetSizeGB: observable,
  canSubmit: computed,
  fetchStorageInfo: action,
  handleSizeChange: action,
  handlePreset: action,
  handleConfirm: action,
  handleClose: action,
});

export default observer(ModifyStorageSizeModal);
